"use client";

import { type ComponentProps, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

interface PasswordInputProps
  extends Omit<ComponentProps<typeof Input>, "type"> {
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  errors?: string[];
}

export const PasswordInput = function PasswordInput({
  ref,
  value,
  onChange,
  onBlur,
  errors,
  className,
  ...props
}: PasswordInputProps & { ref?: React.Ref<HTMLInputElement> }) {
  const [showPassword, setShowPassword] = useState(false);

  const tooltipText = showPassword ? "Hide password" : "Show password";
  const icon = showPassword ? (
    <EyeOff className="size-3.5" />
  ) : (
    <Eye className="size-3.5" />
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          data-slot="password-input"
          ref={ref}
          type={showPassword ? "text" : "password"}
          {...(value === undefined ? {} : { value })}
          onChange={onChange}
          onBlur={onBlur}
          className={cn("pr-24", className)}
          {...props}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {icon}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {errors && errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error) => (
            <p key={error} className="text-sm text-destructive">
              {error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
